/**
 * ParticlePass.ts — instanced billboard renderer for spray + wake particles.
 *
 * Reads the ParticleSystem CPU pool, packs alive particles into a per-frame
 * instance buffer, draws them as instanced quads with premultiplied-alpha
 * blending. No depth write (stays on top of water; depth-tests against scene
 * depth so they don't draw through terrain or boats).
 */

import wgsl from "../shaders/particles.wgsl?raw";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import { ParticleSystem, PARTICLE_STRIDE_BYTES } from "../sim/ParticleSystem";
import { createBufferFromTypedArray } from "../gpu/Buffers";

export class ParticlePass implements RenderPass {
  readonly name = "ParticlePass";

  private device: GPUDevice;
  private pipeline: GPURenderPipeline;
  private bg: GPUBindGroup;
  private cornerBuffer: GPUBuffer;
  private instanceBuffer: GPUBuffer;
  private system: ParticleSystem;

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    system: ParticleSystem,
  ) {
    this.device = device;
    this.system = system;

    // Quad corner positions (-1..+1) for two triangles.
    const corners = new Float32Array([
      -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
    ]);
    this.cornerBuffer = createBufferFromTypedArray(
      device,
      corners,
      GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      "ParticlePass.corners",
    );
    this.instanceBuffer = device.createBuffer({
      label: "ParticlePass.instances",
      size: system.capacity * PARTICLE_STRIDE_BYTES,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });

    const module = device.createShaderModule({
      label: "particles.wgsl",
      code: wgsl,
    });
    const bgl = device.createBindGroupLayout({
      label: "ParticlePass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
      ],
    });
    this.bg = device.createBindGroup({
      label: "ParticlePass.bg",
      layout: bgl,
      entries: [
        { binding: 0, resource: { buffer: camera.buffer } },
        { binding: 1, resource: { buffer: lighting.buffer } },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: "ParticlePass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      vertex: {
        module,
        entryPoint: "vs_main",
        buffers: [
          // Per-instance: 12 floats (48 B), three vec4 attributes.
          {
            arrayStride: PARTICLE_STRIDE_BYTES,
            stepMode: "instance",
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x4" },
              { shaderLocation: 1, offset: 16, format: "float32x4" },
              { shaderLocation: 2, offset: 32, format: "float32x4" },
            ],
          },
          // Per-vertex corner.
          {
            arrayStride: 8,
            stepMode: "vertex",
            attributes: [{ shaderLocation: 3, offset: 0, format: "float32x2" }],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [
          {
            format: rt.current.colorFormat,
            blend: {
              // Premultiplied alpha.
              color: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
              alpha: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
            },
          },
        ],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: false,
        depthCompare: REVERSE_Z_COMPARE,
      },
    });
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const n = this.system.packAlive();
    if (n === 0) return;
    this.device.queue.writeBuffer(
      this.instanceBuffer,
      0,
      this.system.cpuBuffer,
      0,
      n * PARTICLE_STRIDE_BYTES,
    );
    const pass = encoder.beginRenderPass({
      label: "ParticlePass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: frame.renderTarget.depthView,
        depthLoadOp: "load",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bg);
    pass.setVertexBuffer(0, this.instanceBuffer);
    pass.setVertexBuffer(1, this.cornerBuffer);
    pass.draw(6, n, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.cornerBuffer.destroy();
    this.instanceBuffer.destroy();
  }
}
